import { cssVar } from '../../../../../utils/cssVar'
import {
  escHtml,
  encodeSeriesData,
  resolveTooltipTemplate,
  DEFAULT_ALLOCATION_COLOR,
  DEFAULT_OOT_COLOR,
  DEFAULT_TARGET_COLOR,
  DEFAULT_THRESHOLD_COLOR
} from './chartUtils'
import { renderDefaultTooltip } from './DefaultTooltipContent'

function buildMarkerShape (shape, cx, cy, size, fill) {
  const r = size
  switch (shape) {
    case 'circle':
      return { type: 'circle', shape: { cx, cy, r }, style: { fill } }
    case 'square':
      return { type: 'rect', shape: { x: cx - r, y: cy - r, width: r * 2, height: r * 2 }, style: { fill } }
    case 'triangle':
      return {
        type: 'polygon',
        shape: { points: [[cx, cy - r], [cx + r, cy + r], [cx - r, cy + r]] },
        style: { fill }
      }
    case 'diamond':
    default:
      return {
        type: 'polygon',
        shape: { points: [[cx, cy - r], [cx + r * 0.6, cy], [cx, cy + r], [cx - r * 0.6, cy]] },
        style: { fill }
      }
  }
}

export default function buildChartOptions (chartData, config) {
  const {
    tooltip: tooltipOverride,
    tooltipTemplate,
    labelConfiguration,
    targetShape,
    valueFormatter,
    labelFormatter,
    margin
  } = config

  const allocationColor = config.allocationColor || cssVar('--color-primary-500') || DEFAULT_ALLOCATION_COLOR
  const outOfToleranceColor = config.outOfToleranceColor || cssVar('--color-error') || DEFAULT_OOT_COLOR
  const targetColor = config.targetColor || DEFAULT_TARGET_COLOR
  const thresholdColor = config.thresholdColor || DEFAULT_THRESHOLD_COLOR

  if (!chartData.length) return null

  const categories = chartData.map(d => d.name).reverse()
  const reversed = [...chartData].reverse()
  const seriesData = encodeSeriesData(chartData)

  const rawMaxVal = reversed.reduce((max, d) => {
    const candidates = [d.actual, d.target]
    if (d.bandEnd !== null) candidates.push(d.bandEnd)
    return Math.max(max, ...candidates)
  }, 0)
  const maxVal = rawMaxVal > 0 ? rawMaxVal * 1.15 : 1

  return {
    textStyle: {
      fontFamily: cssVar('--font-sans')
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', triggerEmphasis: false },
      formatter: tooltipOverride?.formatter || ((params) => {
        const idx = params[0]?.dataIndex
        const item = reversed[idx]
        if (!item) return escHtml(params[0]?.axisValue || '')
        if (tooltipTemplate) return resolveTooltipTemplate(tooltipTemplate, item, valueFormatter)
        return renderDefaultTooltip(item, { valueFormatter, allocationColor, outOfToleranceColor, targetColor, targetShape, thresholdColor })
      }),
      ...tooltipOverride
    },
    grid: {
      left: margin?.left ?? 20,
      right: margin?.right ?? 80,
      top: margin?.top ?? 20,
      bottom: margin?.bottom ?? 20,
      containLabel: true
    },
    xAxis: {
      type: 'value',
      max: maxVal,
      axisLabel: {
        formatter: (val) => valueFormatter(val),
        fontSize: 11,
        color: cssVar('--text-color-main') || '#666'
      },
      splitLine: {
        lineStyle: { color: cssVar('--color-neutral-200') || '#eee' }
      }
    },
    yAxis: {
      type: 'category',
      data: categories,
      show: true,
      axisLabel: {
        show: true,
        fontSize: 13,
        color: cssVar('--text-color-main') || '#333',
        formatter: (label) => {
          const MAX_LINE_LEN = 16
          if (label.length <= MAX_LINE_LEN) return label
          const words = label.split(' ')
          const lines = []
          let line = ''
          for (const word of words) {
            if (line && (line.length + 1 + word.length) > MAX_LINE_LEN) {
              lines.push(line)
              line = word
            } else {
              line = line ? line + ' ' + word : word
            }
          }
          if (line) lines.push(line)
          return lines.join('\n')
        }
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        type: 'custom',
        renderItem: function (params, api) {
          const idx = api.value(0)
          const actual = api.value(1)
          const target = api.value(2)
          const low = api.value(3)
          const high = api.value(4)
          const isOOT = api.value(5) === 1

          const dataColor = reversed[idx]?.color
          const barColor = dataColor || (isOOT ? outOfToleranceColor : allocationColor)

          const originPt = api.coord([0, idx])
          const actualPt = api.coord([actual, idx])
          const targetPt = api.coord([target, idx])
          const lowPt = api.coord([low, idx])
          const highPt = api.coord([high, idx])

          const bandHeight = api.size([0, 1])[1]
          const barHalf = bandHeight * 0.22
          const markerHalf = bandHeight * 0.18

          const children = []

          children.push({
            type: 'rect',
            shape: {
              x: originPt[0],
              y: actualPt[1] - barHalf,
              width: actualPt[0] - originPt[0],
              height: barHalf * 2,
              r: [0, 3, 3, 0]
            },
            style: { fill: barColor },
            emphasis: { style: { fill: barColor } }
          })

          if (low !== target || high !== target) {
            children.push({
              type: 'line',
              shape: { x1: lowPt[0], y1: lowPt[1], x2: highPt[0], y2: highPt[1] },
              style: { stroke: thresholdColor, lineWidth: 2 }
            })
            children.push({
              type: 'line',
              shape: { x1: lowPt[0], y1: lowPt[1] - markerHalf, x2: lowPt[0], y2: lowPt[1] + markerHalf },
              style: { stroke: thresholdColor, lineWidth: 2 }
            })
            children.push({
              type: 'line',
              shape: { x1: highPt[0], y1: highPt[1] - markerHalf, x2: highPt[0], y2: highPt[1] + markerHalf },
              style: { stroke: thresholdColor, lineWidth: 2 }
            })
          }

          const dSize = markerHalf * 0.7
          children.push(buildMarkerShape(targetShape, targetPt[0], targetPt[1], dSize, targetColor))

          if (labelConfiguration) {
            const barWidth = actualPt[0] - originPt[0]
            const labelX = originPt[0] + barWidth * (labelConfiguration.locationPercent ?? 1) + 6
            children.push({
              type: 'text',
              style: {
                text: labelFormatter(actual),
                x: labelX,
                y: actualPt[1],
                fill: labelConfiguration.fontColor || cssVar('--text-color-main') || '#333',
                fontSize: labelConfiguration.fontSize || 12,
                textVerticalAlign: 'middle'
              }
            })
          }

          return { type: 'group', children }
        },
        encode: { y: 0 },
        data: seriesData,
        z: 2
      }
    ]
  }
}
